import { alpha } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';
import type { Components } from '@mui/material/styles';

// ----------------------------------------------------------------------
// Shared surfaces and controls for the public site and account workspace.
// ----------------------------------------------------------------------

export default function componentsOverrides(theme: Theme): Components<Omit<Theme, 'components'>> {
  return {
    MuiCssBaseline: {
      styleOverrides: {
        '*': {
          margin: 0,
          padding: 0,
          boxSizing: 'border-box',
        },
        html: {
          width: '100%',
          height: '100%',
          WebkitOverflowScrolling: 'touch',
          scrollBehavior: 'smooth',
        },
        body: {
          width: '100%',
          height: '100%',
          backgroundColor: theme.palette.background.default,
        },
        '#root': {
          width: '100%',
          height: '100%',
        },
        input: {
          '&[type=number]': {
            MozAppearance: 'textfield',
            '&::-webkit-outer-spin-button': { margin: 0, WebkitAppearance: 'none' },
            '&::-webkit-inner-spin-button': { margin: 0, WebkitAppearance: 'none' },
          },
        },
        img: {
          display: 'block',
          maxWidth: '100%',
        },
        'a:focus-visible, button:focus-visible': {
          outline: `3px solid ${theme.palette.primary.light}`,
          outlineOffset: 4,
        },
        '::selection': { background: theme.palette.primary.lighter, color: theme.palette.primary.darker },
        '@media (prefers-reduced-motion: reduce)': {
          '*, *::before, *::after': {
            scrollBehavior: 'auto !important',
            animationDuration: '0.01ms !important',
            animationIterationCount: '1 !important',
            transitionDuration: '0.01ms !important',
          },
        },
      },
    },

    MuiPaper: {
      defaultProps: { elevation: 0 },
      variants: [
        {
          props: { variant: 'outlined' },
          style: { borderColor: theme.palette.divider },
        },
      ],
      styleOverrides: {
        root: { backgroundImage: 'none' },
      },
    },

    MuiCard: {
      styleOverrides: {
        root: {
          position: 'relative',
          zIndex: 0, // Fix Safari overflow: hidden with border radius
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          boxShadow: 'none',
        },
      },
    },

    MuiCardHeader: {
      defaultProps: {
        titleTypographyProps: { variant: 'h6' },
        subheaderTypographyProps: { variant: 'body2', marginTop: theme.spacing(0.25) },
      },
      styleOverrides: {
        root: { padding: theme.spacing(2, 2, 0) },
      },
    },

    MuiCardContent: {
      styleOverrides: {
        root: { padding: theme.spacing(2) },
      },
    },

    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: 2,
          paddingLeft: theme.spacing(2.5),
          paddingRight: theme.spacing(2.5),
          '&:hover': { boxShadow: 'none' },
        },
        sizeSmall: { minHeight: 34 },
        sizeMedium: { minHeight: 42 },
        sizeLarge: { minHeight: 50 },
        contained: { boxShadow: 'none' },
        containedPrimary: {
          '&:hover': { backgroundColor: theme.palette.primary.dark },
        },
        outlined: {
          borderWidth: 1.5,
          '&:hover': { borderWidth: 1.5 },
        },
        outlinedInherit: {
          borderColor: theme.palette.grey[400],
          '&:hover': {
            borderColor: theme.palette.text.primary,
            backgroundColor: theme.palette.action.hover,
          },
        },
        textInherit: {
          '&:hover': { backgroundColor: theme.palette.action.hover },
        },
      },
    },

    MuiInputBase: {
      styleOverrides: {
        root: {
          backgroundColor: theme.palette.common.white,
          '&.Mui-disabled': { '& svg': { color: theme.palette.text.disabled } },
        },
        input: {
          '&::placeholder': { opacity: 1, color: theme.palette.text.disabled },
        },
      },
    },

    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.grey[400],
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.text.secondary,
          },
          '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.action.disabledBackground,
          },
        },
      },
    },

    MuiFilledInput: {
      styleOverrides: {
        root: {
          backgroundColor: theme.palette.grey['500_8'],
          '&:hover': { backgroundColor: theme.palette.grey['500_12'] },
          '&.Mui-focused': { backgroundColor: theme.palette.action.focus },
          '&.Mui-disabled': { backgroundColor: theme.palette.action.disabledBackground },
        },
      },
    },

    MuiAutocomplete: {
      styleOverrides: {
        paper: {
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: theme.customShadows.z8,
        },
        listbox: {
          padding: theme.spacing(0.5),
          '& .MuiAutocomplete-option': {
            padding: theme.spacing(0.75, 1),
            borderRadius: theme.shape.borderRadius,
          },
        },
      },
    },

    MuiChip: {
      styleOverrides: {
        root: { borderRadius: 20 },
        sizeSmall: { height: 24, fontSize: theme.typography.pxToRem(12) },
        outlined: {
          borderColor: theme.palette.divider,
          backgroundColor: theme.palette.grey[200],
        },
      },
    },

    MuiDivider: {
      styleOverrides: {
        root: { borderColor: theme.palette.divider },
      },
    },

    MuiTypography: {
      styleOverrides: {
        paragraph: { marginBottom: theme.spacing(1.5) },
        gutterBottom: { marginBottom: theme.spacing(0.75) },
      },
    },

    MuiBackdrop: {
      styleOverrides: {
        root: {
          backgroundColor: alpha(theme.palette.grey[900], 0.6),
          '&.MuiBackdrop-invisible': { backgroundColor: 'transparent' },
        },
      },
    },

    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: theme.shape.borderRadius },
      },
    },

    MuiTabs: {
      styleOverrides: {
        root: { minHeight: 44 },
      },
    },

    MuiTab: {
      styleOverrides: {
        root: {
          minHeight: 44,
          textTransform: 'none',
          fontWeight: 600,
        },
      },
    },

    MuiToggleButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          borderColor: theme.palette.grey[400],
        },
      },
    },
  };
}
